import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { usePuzzles } from '../context/PuzzleContext';
import Header from './Header';
import SEO from './SEO';
import { Search, ArrowRight, BookOpen } from 'lucide-react';

const SearchPage = () => {
  const { puzzles, loading } = usePuzzles();
  const [searchTerm, setSearchTerm] = useState('');

  const results = useMemo(() => {
    if (!searchTerm || searchTerm.length < 2) return [];
    
    const termLower = searchTerm.toLowerCase();
    const matches = [];

    puzzles.forEach(puzzle => {
      const allClues = [...(puzzle.clues.across || []), ...(puzzle.clues.down || [])];
      
      allClues.forEach(clue => {
        if (
          clue.text.toLowerCase().includes(termLower) || 
          clue.answer.toLowerCase().includes(termLower)
        ) {
          matches.push({
            puzzleDate: puzzle.date,
            clue
          });
        }
      });
    });

    return matches.slice(0, 50); // Limit results
  }, [puzzles, searchTerm]);

  return (
    <div className="flex flex-col min-h-screen">
      <SEO 
        title="Search Clues" 
        description="Search for any Pocket Grids clue or answer in our database."
        url={window.location.origin + '/search'}
        noindex={true}
      />
      
      <Header date={new Date().toISOString()} title="Search" />

      <main className="flex-grow max-w-3xl mx-auto px-4 w-full pb-20">
        <div className="text-center mb-8">
           <h2 className="text-3xl font-black text-gray-900 mb-2">Search Clues</h2>
           <p className="text-gray-500">Find answers by clue text or answer keyword.</p>
        </div>

        <div className="relative mb-10">
          <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            className="block w-full pl-12 pr-4 py-4 border border-gray-200 rounded-xl bg-white shadow-sm text-lg focus:ring-2 focus:ring-brand-blue focus:border-brand-blue outline-none transition-all"
            placeholder="Search e.g. 'feline' or 'CAT'..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            autoFocus
          />
        </div>

        <div className="space-y-4">
          {searchTerm.length >= 2 && results.length === 0 && (
             <div className="text-center py-12 text-gray-500 bg-white rounded-xl border border-gray-100">
               No matches found for "{searchTerm}"
             </div>
          )}

          {results.map(({ puzzleDate, clue }, idx) => (
            <Link 
              key={`${puzzleDate}-${clue.id}-${idx}`}
              to={`/pocket-grid-${puzzleDate}/${clue.slug || ''}`}
              className="block bg-white border border-gray-200 rounded-xl p-5 hover:border-brand-blue hover:shadow-md transition-all"
            >
              <div className="flex justify-between items-start mb-2">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md bg-gray-100 text-gray-600 text-xs font-bold uppercase tracking-wider">
                  {new Date(puzzleDate).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                </span>
                <span className="text-xs font-medium text-brand-blue flex items-center gap-1">
                  View <ArrowRight className="w-3 h-3" />
                </span>
              </div>
              <div className="font-serif text-lg text-gray-900 font-medium mb-1">
                {clue.text}
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <BookOpen className="w-4 h-4" />
                <span>Answer: <strong className="text-gray-700">{clue.answer}</strong></span>
              </div>
            </Link>
          ))}
        </div>
      </main>
    </div>
  );
};

export default SearchPage;

